import jwtDecode from "jwt-decode";
import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import {
  Box, Button, Chip, CircularProgress, Container,
  Dialog, DialogActions, DialogContent, DialogTitle,
  Divider, Drawer, FormControlLabel, IconButton, Paper,
  Stack, Switch, TextField, Tooltip, Typography,
} from "@mui/material";
import {
  MdAdd, MdCheck, MdContentCopy, MdDelete, MdEdit,
  MdManageAccounts, MdMarkEmailRead, MdPeople, MdRefresh, MdSearch,
} from "react-icons/md";
import AddUsersDialog from "../components/modals/AddUsersDialog";
import UsersEditModal from "../components/modals/UsersEditModal";
import AdminProfileModal from "../components/modals/AdminProfileModal";
import {
  activateUser, deactivateUser, deleteUser, getAllUsers,
} from "../store/actions/users";
import { refreshToken } from "../store/actions/auth";
import Colors from "../../assets/styles/Colors";
import { db } from "../../firebase/firebase-config";
import { ADMIN_API_URL } from "../variables/globals";

// ── IB helpers ────────────────────────────────────────────────────────────────

const REFERRAL_BASE = "https://nucoreapp.com/?ref=";
const PACKAGES = [
  { key: "access", label: "ACCESS", price: 499,  base: 499  },
  { key: "pro",    label: "PRO",    price: 799,  base: 799  },
  { key: "elite",  label: "ELITE",  price: 1350, base: 1000 },
];
function commL1(base)       { return (base * 0.30).toFixed(2); }
function commL2(base)       { return (base * 0.10).toFixed(2); }
function commPlatform(base) { return (base * 0.60).toFixed(2); }
function fmtUsdt(n)         { return typeof n === "number" ? n.toFixed(2) : "0.00"; }

function generateIbCode(name, lastname) {
  const init = ((name?.[0] ?? "") + (lastname?.[0] ?? "")).toUpperCase();
  const rand = Math.random().toString(36).toUpperCase().slice(2, 6);
  return `IB-${init}-${rand}`;
}

async function loadCommissionStats(uid) {
  try {
    const snap = await db.collection("commissions").where("partnerId", "==", uid).get();
    let l1Total = 0, l2Total = 0, pendingAmount = 0, pendingCount = 0;
    snap.docs.forEach(d => {
      const c = d.data();
      const amt = c.commissionAmount ?? 0;
      if (c.commissionType === "level2") l2Total += amt; else l1Total += amt;
      if (c.status === "pending_wallet") { pendingAmount += amt; pendingCount++; }
    });
    return { l1Total, l2Total, pendingAmount, pendingCount, total: snap.size };
  } catch { return null; }
}

function CopyBtn({ value, small }) {
  const [copied, setCopied] = useState(false);
  function handleCopy(e) {
    e.stopPropagation();
    navigator.clipboard.writeText(value).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 1800);
  }
  return (
    <Tooltip title={copied ? "¡Copiado!" : "Copiar"}>
      <Button
        size="small" onClick={handleCopy}
        startIcon={copied ? <MdCheck size={14} /> : <MdContentCopy size={14} />}
        sx={{
          minWidth: 0, px: 1, py: 0.25,
          color: copied ? Colors.success : Colors.textMuted,
          fontSize: small ? 10 : 11,
          textTransform: "none",
          border: "1px solid", borderColor: Colors.borderSecondary,
          borderRadius: 1.5,
          "&:hover": { borderColor: Colors.primary, color: Colors.primary },
        }}
      >
        {copied ? "OK" : "COPIAR"}
      </Button>
    </Tooltip>
  );
}

// ── Status helpers ────────────────────────────────────────────────────────────

const isValidToken = (token) => {
  if (!token) return false;
  try { const d = jwtDecode(token); return Math.floor(Date.now() / 1000) < d.exp; }
  catch { return false; }
};

const PLAN_BADGE = {
  elite:  { label: "Elite",  color: Colors.warning },
  pro:    { label: "Pro",    color: Colors.primaryLight },
  access: { label: "Access", color: Colors.primary },
  luxe:   { label: "Elite",  color: Colors.warning },
  socio:  { label: "Pro",    color: Colors.primaryLight },
  basic:  { label: "Access", color: Colors.primary },
};

function getMembership(user) {
  if (user.isDisabled) return { label: "Desactivado", color: Colors.error };
  const planKey = user.currentPlanKey || (user.isGoldPlan ? "elite" : user.isActive ? "access" : null);
  const badge   = planKey ? PLAN_BADGE[planKey] : null;
  if (badge) {
    if (!user.isActive) return { label: `${badge.label} (inactivo)`, color: Colors.textMuted };
    return badge;
  }
  return { label: "Inactivo", color: Colors.textMuted };
}

function fmtDate(v) {
  if (!v?.seconds) return "—";
  const d = new Date(v.seconds * 1000 + (v.nanoseconds ?? 0) / 1e6);
  return isNaN(d) ? "—" : d.toLocaleDateString();
}

const na = (v) => (v != null && v !== "" ? v : "—");
const displayName = (u) =>
  [u.name || u.nombre, u.lastname || u.apellido].filter(Boolean).join(" ") || u.email || u.id;

// ── Filter config ─────────────────────────────────────────────────────────────

const STATUS_FILTERS = [
  { key: "all",      label: "Todos" },
  { key: "active",   label: "Activos" },
  { key: "inactive", label: "Inactivos" },
  { key: "ib",       label: "IB Partners" },
  { key: "disabled", label: "Desactivados" },
];

// ── Main component ─────────────────────────────────────────────────────────────

export default function UsersView() {
  const dispatch = useDispatch();
  const { users } = useSelector((s) => s.users);
  const { idToken, isAdmin, isSuperAdmin, isAlmighty } = useSelector((s) => s.auth);

  const isFirstUse = useRef(true);
  const [loading, setLoading]   = useState(true);
  const [search, setSearch]     = useState("");
  const [roleFilter, setRoleFilter] = useState("all");

  // User modals
  const [addOpen, setAddOpen]               = useState(false);
  const [editModalOpen, setEditModalOpen]   = useState(false);
  const [selectedItem, setSelectedItem]     = useState(null);
  const [profileOpen, setProfileOpen]       = useState(false);

  // Activate/deactivate confirm
  const [confirmTarget, setConfirmTarget] = useState(null);
  const [confirming, setConfirming]       = useState(false);
  const [confirmError, setConfirmError]   = useState("");

  // Send credentials
  const [sendingCreds, setSendingCreds] = useState(null); // uid being sent
  const [credsSent, setCredsSent]       = useState(null); // uid last succeeded

  // IB drawer
  const [ibDrawerOpen, setIbDrawerOpen]       = useState(false);
  const [ibDraft, setIbDraft]                 = useState(null);
  const [ibCommStats, setIbCommStats]         = useState(null);
  const [ibStatsLoading, setIbStatsLoading]   = useState(false);
  const [ibSaving, setIbSaving]               = useState(false);
  const [ibSaveError, setIbSaveError]         = useState("");

  const getUsersFromFB = useCallback(() => {
    setLoading(true);
    dispatch(getAllUsers());
    setTimeout(() => setLoading(false), 1000);
  }, [dispatch]);

  useEffect(() => {
    if (isFirstUse.current) { getUsersFromFB(); isFirstUse.current = false; }
  }, [getUsersFromFB]);

  const filtered = useMemo(() => {
    let list = users ?? [];
    if (roleFilter === "active")   list = list.filter(u => u.isActive && !u.isDisabled);
    else if (roleFilter === "inactive") list = list.filter(u => !u.isActive && !u.isDisabled);
    else if (roleFilter === "ib")  list = list.filter(u => u.isIBPartner);
    else if (roleFilter === "disabled") list = list.filter(u => u.isDisabled);
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(u =>
        (u.name || u.nombre || "").toLowerCase().includes(q) ||
        (u.lastname || u.apellido || "").toLowerCase().includes(q) ||
        (u.email || "").toLowerCase().includes(q)
      );
    }
    return list;
  }, [users, search, roleFilter]);

  // ── Send credentials ──────────────────────────────────────────────────────

  async function handleSendCredentials(user) {
    const uid = user.id || user.uid;
    setSendingCreds(uid);
    try {
      const token = isValidToken(idToken) ? idToken : await refreshToken();
      const res = await fetch(`${ADMIN_API_URL}/sendCredentials`, {
        method:  "POST",
        headers: { "Content-Type": "application/json", Authorization: "Bearer " + token },
        body:    JSON.stringify({ uid }),
      });
      const data = await res.json();
      if (data.ok) {
        setCredsSent(uid);
        setTimeout(() => setCredsSent(null), 3000);
      } else {
        console.error("sendCredentials failed:", data.error);
      }
    } catch (err) {
      console.error("sendCredentials error:", err);
    } finally {
      setSendingCreds(null);
    }
  }

  // ── Activate / deactivate ──────────────────────────────────────────────────

  const openConfirm = (action, user) => { setConfirmError(""); setConfirmTarget({ action, user }); };

  const handleConfirm = async () => {
    if (!confirmTarget) return;
    setConfirming(true); setConfirmError("");
    const { action, user } = confirmTarget;
    try {
      const token = isValidToken(idToken) ? idToken : await refreshToken();
      const res = await deleteUser({ uid: user.id, disabled: action === "deactivate" }, token);
      if (res.ok) {
        if (action === "deactivate") await deactivateUser(user.id);
        else await activateUser(user.id);
        setConfirmTarget(null);
        getUsersFromFB();
      } else { setConfirmError("Error al procesar la solicitud."); }
    } catch { setConfirmError("Hubo un error. Intenta de nuevo."); }
    finally { setConfirming(false); }
  };

  // ── IB drawer ─────────────────────────────────────────────────────────────

  async function openIbDrawer(user) {
    setIbSaveError(""); setIbCommStats(null);
    const uid = user.id || user.uid;
    setIbDraft({
      uid,
      displayName:     displayName(user),
      email:           user.email || "",
      ibCode:          user.ibCode ?? "",
      walletAddress:   user.walletAddress ?? "",
      walletCurrency:  user.walletCurrency ?? "usdttrc20",
      ibActive:        user.ibActive !== false,
      name:            user.name    || user.nombre   || "",
      lastname:        user.lastname || user.apellido || "",
      recruitedByCode: user.recruitedByCode ?? "",
      isIBPartner:     !!user.isIBPartner,
    });
    setIbDrawerOpen(true);
    setIbStatsLoading(true);
    const stats = await loadCommissionStats(uid);
    setIbCommStats(stats);
    setIbStatsLoading(false);
  }

  function closeIbDrawer() {
    if (ibSaving) return;
    setIbDrawerOpen(false); setIbDraft(null); setIbCommStats(null);
  }

  function setIb(key, val) { setIbDraft(prev => ({ ...prev, [key]: val })); }

  async function handleIbSave() {
    setIbSaving(true); setIbSaveError("");
    try {
      const code = (ibDraft.ibCode.trim() || generateIbCode(ibDraft.name, ibDraft.lastname)).toUpperCase();
      const recruitedBy = ibDraft.recruitedByCode.trim().toUpperCase() || null;
      await db.collection("users").doc(ibDraft.uid).set({
        isIBPartner:     true,
        ibActive:        Boolean(ibDraft.ibActive),
        ibCode:          code,
        walletAddress:   ibDraft.walletAddress.trim() || null,
        walletCurrency:  ibDraft.walletCurrency,
        recruitedByCode: recruitedBy,
        referredBy:      recruitedBy,
      }, { merge: true });
      setIbDrawerOpen(false); setIbDraft(null); setIbCommStats(null);
      getUsersFromFB();
    } catch (e) { setIbSaveError("No se pudo guardar: " + e.message); }
    finally { setIbSaving(false); }
  }

  async function handleRevokeIb(uid, name) {
    if (!window.confirm(`¿Quitar estatus IB a ${name}?`)) return;
    await db.collection("users").doc(uid).update({ isIBPartner: false, ibActive: false });
    getUsersFromFB();
  }

  const canModify = isSuperAdmin || isAlmighty;

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <Box sx={{ width: "100%", py: 3 }}>
      <Container maxWidth="xl">

        {/* Header */}
        <Paper sx={{ mb: 3, p: 2.5, borderRadius: 3, background: `linear-gradient(135deg, ${Colors.bgSecondary} 0%, ${Colors.bgTertiary} 100%)`, border: `1px solid ${Colors.borderSecondary}` }}>
          <Stack direction="row" alignItems="center" justifyContent="space-between">
            <Box>
              <Typography variant="h5" sx={{ color: Colors.textPrimary }}>Usuarios</Typography>
              <Typography variant="body2" sx={{ color: Colors.textSecondary }}>
                Gestión de usuarios y socios IB registrados en la plataforma
              </Typography>
            </Box>
            <Stack direction="row" spacing={1.5}>
              <Button
                variant="outlined" startIcon={<MdManageAccounts />}
                onClick={() => setProfileOpen(true)}
                sx={{ borderColor: Colors.borderSecondary, color: Colors.textSecondary, textTransform: "none", borderRadius: 2 }}
              >
                Mi Perfil
              </Button>
              <Button
                variant="contained" startIcon={<MdAdd />}
                onClick={() => setAddOpen(true)}
                sx={{ backgroundColor: Colors.primary, color: Colors.white, textTransform: "none", borderRadius: 2 }}
              >
                Nuevo Usuario
              </Button>
            </Stack>
          </Stack>
        </Paper>

        {/* Filters */}
        <Stack spacing={1.5} sx={{ mb: 2 }}>
          {/* Search */}
          <Stack direction="row" spacing={2} alignItems="center">
            <TextField
              size="small"
              placeholder="Buscar por nombre o correo..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              InputProps={{ startAdornment: <MdSearch style={{ marginRight: 6, color: Colors.textSecondary }} /> }}
              sx={{
                minWidth: 300,
                "& .MuiOutlinedInput-root": {
                  backgroundColor: Colors.bgSecondary, color: Colors.textPrimary,
                  "& fieldset": { borderColor: Colors.borderSecondary },
                  "&:hover fieldset": { borderColor: Colors.borderPrimary },
                },
              }}
            />
            <IconButton onClick={getUsersFromFB} sx={{ color: Colors.textSecondary }}>
              <MdRefresh size={20} />
            </IconButton>
            {users && (
              <Typography variant="caption" sx={{ color: Colors.textSecondary }}>
                {filtered.length} de {users.length} usuarios
              </Typography>
            )}
          </Stack>

          {/* Status/role filter chips */}
          <Stack direction="row" spacing={1} flexWrap="wrap">
            {STATUS_FILTERS.map(f => {
              const active = roleFilter === f.key;
              return (
                <Chip
                  key={f.key}
                  label={f.key === "ib" ? <Stack direction="row" alignItems="center" gap={0.5}><MdPeople size={13} />{f.label}</Stack> : f.label}
                  size="small"
                  onClick={() => setRoleFilter(f.key)}
                  sx={{
                    cursor: "pointer",
                    backgroundColor: active ? Colors.primary + "33" : Colors.bgSecondary,
                    color: active ? Colors.primary : Colors.textSecondary,
                    border: `1px solid ${active ? Colors.primary : Colors.borderSecondary}`,
                    fontWeight: active ? 700 : 400,
                    transition: "all 0.15s",
                    "&:hover": { borderColor: Colors.primary, color: Colors.primary },
                  }}
                />
              );
            })}
          </Stack>
        </Stack>

        {loading && (
          <Box sx={{ display: "flex", justifyContent: "center", py: 6 }}>
            <CircularProgress sx={{ color: Colors.primary }} />
          </Box>
        )}

        {!loading && (
          <Paper sx={{ borderRadius: 3, backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}`, overflow: "hidden" }}>

            {/* Table header */}
            <Box sx={{ px: 3, py: 1.5, backgroundColor: Colors.bgTertiary, borderBottom: `1px solid ${Colors.borderSecondary}` }}>
              <Stack direction="row" spacing={1} alignItems="center">
                <Box sx={{ flex: 2 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Nombre</Typography></Box>
                <Box sx={{ flex: 2 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Correo</Typography></Box>
                <Box sx={{ flex: 1 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Membresía</Typography></Box>
                <Box sx={{ flex: 1 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>IB</Typography></Box>
                <Box sx={{ flex: 1 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Teléfono</Typography></Box>
                <Box sx={{ flex: 1 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Registro</Typography></Box>
                <Box sx={{ width: 150, flexShrink: 0 }}><Typography variant="subtitle2" sx={{ color: Colors.textSecondary }}>Acciones</Typography></Box>
              </Stack>
            </Box>

            {filtered.length === 0 && (
              <Box sx={{ px: 3, py: 4, textAlign: "center" }}>
                <Typography sx={{ color: Colors.textSecondary }}>No se encontraron usuarios.</Typography>
              </Box>
            )}

            {filtered.map((user) => {
              const mem = getMembership(user);
              const isIB = !!user.isIBPartner;
              return (
                <Box
                  key={user.id}
                  sx={{
                    px: 3, py: 1.75,
                    borderBottom: `1px solid ${Colors.borderSecondary}`,
                    "&:hover": { backgroundColor: Colors.bgTertiary },
                    transition: "background-color 0.15s",
                    position: "relative",
                  }}
                >
                  {isIB && (
                    <Box sx={{ position: "absolute", left: 0, top: 0, bottom: 0, width: 3, backgroundColor: user.ibActive !== false ? Colors.success : Colors.error }} />
                  )}
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Box sx={{ flex: 2 }}>
                      <Typography variant="body2" sx={{ color: Colors.textPrimary }}>
                        {na(user.name || user.nombre)} {user.lastname || user.apellido || ""}
                      </Typography>
                    </Box>
                    <Box sx={{ flex: 2 }}>
                      <Typography variant="body2" sx={{ color: Colors.textSecondary }}>{na(user.email)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Chip
                        label={mem.label} size="small"
                        sx={{ backgroundColor: mem.color + "22", color: mem.color, border: `1px solid ${mem.color}44`, fontWeight: 700, fontSize: "0.7rem" }}
                      />
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      {isIB ? (
                        <Stack spacing={0.25}>
                          <Chip
                            label={user.ibCode || "IB"} size="small"
                            sx={{ backgroundColor: "#1a0a3e", color: "#c084fc", border: "none", fontWeight: 700, fontSize: "0.65rem", maxWidth: 90 }}
                          />
                          {!user.ibActive && (
                            <Typography variant="caption" sx={{ color: Colors.error, fontSize: "0.6rem" }}>Inactivo</Typography>
                          )}
                        </Stack>
                      ) : (
                        <Typography variant="caption" sx={{ color: Colors.textMuted }}>—</Typography>
                      )}
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="body2" sx={{ color: Colors.textSecondary }}>{na(user.phone)}</Typography>
                    </Box>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="caption" sx={{ color: Colors.textSecondary }}>{fmtDate(user.cdate)}</Typography>
                    </Box>
                    <Stack direction="row" spacing={0.5} sx={{ width: 150, flexShrink: 0 }} alignItems="center">
                      <IconButton
                        size="small"
                        onClick={() => { setSelectedItem(user); setEditModalOpen(true); }}
                        sx={{ color: Colors.textSecondary }}
                        title="Editar usuario"
                      >
                        <MdEdit size={16} />
                      </IconButton>
                      {user.isDisabled ? (
                        <IconButton size="small" onClick={() => openConfirm("activate", user)} disabled={!canModify} sx={{ color: Colors.success }} title="Activar">
                          <MdAdd size={16} />
                        </IconButton>
                      ) : (
                        <IconButton size="small" onClick={() => openConfirm("deactivate", user)} disabled={!canModify} sx={{ color: Colors.error }} title="Desactivar">
                          <MdDelete size={16} />
                        </IconButton>
                      )}
                      <Tooltip title={credsSent === (user.id || user.uid) ? "¡Enviado!" : "Enviar credenciales"}>
                        <span>
                          <IconButton
                            size="small"
                            onClick={() => handleSendCredentials(user)}
                            disabled={sendingCreds === (user.id || user.uid)}
                            sx={{
                              color: credsSent === (user.id || user.uid) ? Colors.success : Colors.textMuted,
                              "&:hover": { color: Colors.success },
                            }}
                          >
                            {sendingCreds === (user.id || user.uid)
                              ? <CircularProgress size={14} sx={{ color: Colors.textMuted }} />
                              : <MdMarkEmailRead size={16} />}
                          </IconButton>
                        </span>
                      </Tooltip>
                      <Tooltip title={isIB ? "Gestionar IB" : "Registrar como IB"}>
                        <IconButton size="small" onClick={() => openIbDrawer(user)} sx={{ color: isIB ? "#c084fc" : Colors.textMuted, "&:hover": { color: "#c084fc" } }}>
                          <MdPeople size={16} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>
                </Box>
              );
            })}
          </Paper>
        )}

        {/* ── IB Management Drawer ─────────────────────────────────────────── */}
        <Drawer anchor="right" open={ibDrawerOpen} onClose={closeIbDrawer} PaperProps={{ sx: { top: "72px", height: "calc(100% - 72px)" } }}>
          <Box sx={{ width: { xs: "100vw", sm: 500 }, p: 3, backgroundColor: Colors.bgSecondary, height: "100%", overflowY: "auto" }}>
            {ibDraft && (
              <Stack spacing={2.5}>
                {/* Header */}
                <Box>
                  <Typography variant="h6" sx={{ color: Colors.textPrimary }}>
                    {ibDraft.isIBPartner ? "Socio IB" : "Registrar como Socio IB"}
                  </Typography>
                  <Typography variant="body2" sx={{ color: Colors.textSecondary, mt: 0.5 }}>
                    {ibDraft.displayName} · {ibDraft.email}
                  </Typography>
                </Box>

                {ibSaveError && <Typography variant="body2" color="error">{ibSaveError}</Typography>}

                {/* Referral link */}
                <Box sx={{ p: 2, borderRadius: 2, backgroundColor: Colors.bgTertiary, border: `1px solid ${Colors.borderSecondary}` }}>
                  <Typography variant="caption" sx={{ color: Colors.textSecondary, letterSpacing: 1 }}>LINK DE REFERIDO</Typography>
                  <Stack direction="row" alignItems="center" spacing={1} sx={{ mt: 0.75 }}>
                    <Typography variant="body2" sx={{ color: Colors.textPrimary, fontFamily: "monospace", wordBreak: "break-all", flex: 1, fontSize: 12 }}>
                      {REFERRAL_BASE}{ibDraft.ibCode || "—"}
                    </Typography>
                    {ibDraft.ibCode && <CopyBtn value={`${REFERRAL_BASE}${ibDraft.ibCode}`} />}
                  </Stack>
                </Box>

                {/* IB Code */}
                <TextField
                  label="Código IB"
                  value={ibDraft.ibCode}
                  onChange={e => setIb("ibCode", e.target.value)}
                  fullWidth variant="standard"
                  inputProps={{ style: { textTransform: "uppercase" } }}
                  helperText="Se genera automáticamente si se deja vacío"
                  FormHelperTextProps={{ sx: { color: Colors.textMuted } }}
                />

                {/* Recruited by */}
                <TextField
                  label="Captado por (código IB del reclutador)"
                  value={ibDraft.recruitedByCode}
                  onChange={e => setIb("recruitedByCode", e.target.value.toUpperCase())}
                  fullWidth variant="standard"
                  placeholder="Ej: IB-AB-XY12"
                  helperText="Determina quién recibe la comisión de Nivel 2."
                  FormHelperTextProps={{ sx: { color: Colors.textMuted } }}
                />

                <Divider sx={{ borderColor: Colors.borderSecondary }} />

                {/* Package commission breakdown */}
                <Box>
                  <Typography variant="caption" sx={{ color: Colors.textSecondary, letterSpacing: 1 }}>COMISIONES POR PAQUETE</Typography>
                  <Box sx={{ mt: 1.5, borderRadius: 2, overflow: "hidden", border: `1px solid ${Colors.borderSecondary}` }}>
                    <Stack direction="row" sx={{ backgroundColor: Colors.bgTertiary, px: 2, py: 1 }}>
                      {["Paquete", "Precio", "Base", "N1 (30%)", "N2 (10%)", "Plat."].map(h => (
                        <Typography key={h} variant="caption" sx={{ flex: 1, color: Colors.textMuted, fontSize: 10 }}>{h.toUpperCase()}</Typography>
                      ))}
                    </Stack>
                    {PACKAGES.map((pkg, i) => (
                      <Stack key={pkg.key} direction="row" sx={{ px: 2, py: 1.5, borderTop: i > 0 ? `1px solid ${Colors.borderSecondary}` : "none", "&:hover": { backgroundColor: Colors.bgTertiary } }}>
                        <Typography variant="body2" sx={{ flex: 1, color: Colors.textPrimary, fontWeight: 700 }}>{pkg.label}</Typography>
                        <Typography variant="body2" sx={{ flex: 1, color: Colors.textSecondary }}>${pkg.price}</Typography>
                        <Typography variant="body2" sx={{ flex: 1, color: Colors.textMuted, fontSize: 12 }}>${pkg.base}</Typography>
                        <Typography variant="body2" sx={{ flex: 1, color: "#34d399", fontWeight: 700 }}>${commL1(pkg.base)}</Typography>
                        <Typography variant="body2" sx={{ flex: 1, color: "#60a5fa", fontWeight: 700 }}>${commL2(pkg.base)}</Typography>
                        <Typography variant="body2" sx={{ flex: 1, color: Colors.textMuted, fontSize: 12 }}>${commPlatform(pkg.base)}</Typography>
                      </Stack>
                    ))}
                  </Box>
                  <Typography variant="caption" sx={{ color: Colors.textMuted, mt: 0.75, display: "block" }}>
                    * Elite: base comisionable sobre $1,000 USD (precio de venta $1,350).
                  </Typography>
                </Box>

                <Divider sx={{ borderColor: Colors.borderSecondary }} />

                {/* Commission stats */}
                <Box>
                  <Typography variant="caption" sx={{ color: Colors.textSecondary, letterSpacing: 1 }}>RESUMEN DE COMISIONES</Typography>
                  {ibStatsLoading && <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}><CircularProgress size={20} /></Box>}
                  {!ibStatsLoading && ibCommStats && (
                    <Stack spacing={1} sx={{ mt: 1.5 }}>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: Colors.textSecondary }}>Nivel 1 (ventas directas)</Typography>
                        <Typography variant="body2" sx={{ color: "#34d399", fontWeight: 700 }}>{fmtUsdt(ibCommStats.l1Total)} USDT</Typography>
                      </Stack>
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: Colors.textSecondary }}>Nivel 2 (ventas indirectas)</Typography>
                        <Typography variant="body2" sx={{ color: "#60a5fa", fontWeight: 700 }}>{fmtUsdt(ibCommStats.l2Total)} USDT</Typography>
                      </Stack>
                      <Divider sx={{ borderColor: Colors.borderSecondary }} />
                      <Stack direction="row" justifyContent="space-between">
                        <Typography variant="body2" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>Total comisiones</Typography>
                        <Typography variant="body2" sx={{ color: Colors.textPrimary, fontWeight: 700 }}>
                          {fmtUsdt(ibCommStats.l1Total + ibCommStats.l2Total)} USDT
                        </Typography>
                      </Stack>
                      {ibCommStats.pendingCount > 0 && (
                        <Box sx={{ mt: 1, p: 1.5, borderRadius: 2, backgroundColor: `${Colors.warning}18`, border: `1px solid ${Colors.warning}60` }}>
                          <Typography variant="caption" sx={{ color: Colors.warning, fontWeight: 700 }}>
                            ⚠ {ibCommStats.pendingCount} comisión{ibCommStats.pendingCount > 1 ? "es" : ""} pendiente{ibCommStats.pendingCount > 1 ? "s" : ""}
                          </Typography>
                          <Typography variant="caption" sx={{ color: Colors.warning, display: "block", mt: 0.25 }}>
                            {fmtUsdt(ibCommStats.pendingAmount)} USDT — se liberan al vincular wallet
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                  )}
                  {!ibStatsLoading && !ibCommStats && (
                    <Typography variant="caption" sx={{ color: Colors.textMuted, mt: 1, display: "block" }}>Sin comisiones registradas aún.</Typography>
                  )}
                </Box>

                <Divider sx={{ borderColor: Colors.borderSecondary }} />

                {/* Wallet */}
                <TextField
                  label="Dirección de billetera"
                  value={ibDraft.walletAddress}
                  onChange={e => setIb("walletAddress", e.target.value)}
                  fullWidth variant="standard"
                  placeholder="Dejar vacío si no tiene"
                />
                <TextField
                  select label="Moneda de pago"
                  value={ibDraft.walletCurrency}
                  onChange={e => setIb("walletCurrency", e.target.value)}
                  fullWidth variant="standard"
                  SelectProps={{ native: true }}
                >
                  <option value="usdttrc20">USDT TRC-20</option>
                  <option value="usdterc20">USDT ERC-20</option>
                  <option value="btc">BTC</option>
                  <option value="eth">ETH</option>
                </TextField>

                <FormControlLabel
                  control={<Switch checked={Boolean(ibDraft.ibActive)} onChange={e => setIb("ibActive", e.target.checked)} color="primary" size="small" />}
                  label={<Typography variant="body2" sx={{ color: Colors.textPrimary }}>Socio activo</Typography>}
                />

                <Stack direction="row" spacing={1.5} sx={{ pt: 1 }}>
                  <Button
                    variant="outlined" onClick={closeIbDrawer} disabled={ibSaving}
                    sx={{ borderColor: Colors.borderPrimary, color: Colors.textSecondary }}
                  >
                    Cancelar
                  </Button>
                  {ibDraft.isIBPartner && (
                    <Button
                      variant="outlined" disabled={ibSaving}
                      onClick={() => { handleRevokeIb(ibDraft.uid, ibDraft.displayName); closeIbDrawer(); }}
                      sx={{ borderColor: Colors.error + "80", color: Colors.error, "&:hover": { borderColor: Colors.error } }}
                    >
                      Quitar IB
                    </Button>
                  )}
                  <Button
                    variant="contained" onClick={handleIbSave} disabled={ibSaving}
                    sx={{ backgroundColor: Colors.primary, color: Colors.white, flex: 1, "&:hover": { backgroundColor: Colors.primaryDark } }}
                  >
                    {ibSaving ? "Guardando…" : ibDraft.isIBPartner ? "Guardar" : "Activar como IB"}
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>
        </Drawer>

        {/* ── Modals ──────────────────────────────────────────────────────── */}
        <AddUsersDialog open={addOpen} onClose={() => setAddOpen(false)} refresh={getUsersFromFB} isAdmin={isAdmin} />
        {selectedItem && (
          <UsersEditModal
            item={selectedItem} open={editModalOpen}
            onClose={() => { setSelectedItem(null); setEditModalOpen(false); }}
            refresh={getUsersFromFB}
          />
        )}
        <AdminProfileModal open={profileOpen} onClose={() => setProfileOpen(false)} />

        {/* Confirm activate / deactivate */}
        <Dialog
          open={!!confirmTarget}
          onClose={() => !confirming && setConfirmTarget(null)}
          PaperProps={{ sx: { backgroundColor: Colors.bgSecondary, border: `1px solid ${Colors.borderSecondary}` } }}
        >
          <DialogTitle sx={{ color: Colors.textPrimary }}>
            {confirmTarget?.action === "activate" ? "Activar usuario" : "Desactivar usuario"}
          </DialogTitle>
          <DialogContent>
            <Typography sx={{ color: Colors.textSecondary }}>
              {confirmTarget?.action === "activate"
                ? `¿Activar a ${confirmTarget?.user?.name ?? ""} ${confirmTarget?.user?.lastname ?? ""} (${confirmTarget?.user?.email})?`
                : `¿Desactivar a ${confirmTarget?.user?.name ?? ""} ${confirmTarget?.user?.lastname ?? ""} (${confirmTarget?.user?.email})?`}
            </Typography>
            {confirmError && <Typography variant="body2" sx={{ color: Colors.error, mt: 1 }}>{confirmError}</Typography>}
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmTarget(null)} disabled={confirming} sx={{ color: Colors.textSecondary }}>Cancelar</Button>
            <Button
              onClick={handleConfirm} disabled={confirming}
              sx={{ color: confirmTarget?.action === "activate" ? Colors.success : Colors.error }}
            >
              {confirming ? "Procesando..." : confirmTarget?.action === "activate" ? "Activar" : "Desactivar"}
            </Button>
          </DialogActions>
        </Dialog>

      </Container>
    </Box>
  );
}
